import Factory from '@leverege/factory'

import Sort from './Sort.js'
import Util from './Util.js'

const sortDefault = ( valA, valB ) => {
  let a = valA
  let b = valB

  // force null and undefined to the bottom
  a = a === null || a === undefined ? '' : a
  b = b === null || b === undefined ? '' : b
  // force any string values to lowercase
  a = typeof a === 'string' ? a.toLowerCase() : a
  b = typeof b === 'string' ? b.toLowerCase() : b
  // Return either 1 or -1 to indicate a sort priority
  if ( a > b ) {
    return 1
  }
  if ( a < b ) {
    return -1
  }

  // returning 0, undefined or any falsey value will use subsequent sorts or
  // the index as a tiebreaker
  return 0
}

export default class SortController {
  constructor( stateKey, lookup, options = {} ) {
    const { type = 'default', idKey = 'id', idFunc } = options

    this.stateKey = stateKey
    this.lookup = lookup
    this.type = type
    this.idKey = idKey
    this.idFunc = idFunc
  }

  static cache = {}

  static SortFactory = new Factory( {
    name : 'SortFactory',
    registry : {
      default : sortDefault
    },
    defaultObject : sortDefault,
    strategy : Factory.LOOKUP
  } )

  key( column ) {
    // If we've been given a sort key, just return it
    if ( typeof column === 'string' ) {
      return column
    }

    if ( this.idFunc ) {
      return this.idFunc( column )
    }

    return this.idKey ? column[this.idKey] : column && column.id
  }

  isColumnSorted( column, sorts ) {
    const key = this.key( column )
    const item = sorts[key]

    return !!( item )
  }

  needsUpdate( data, sortModel ) {
    return SortController.cache[`${this.stateKey}.lastData`] !== data ||
    SortController.cache[`${this.stateKey}.lastSortModel`] !== sortModel
  }

  sort( sortModel, data, ) {
    if ( !this.needsUpdate( data, sortModel ) ) {
      return SortController.cache[`${this.stateKey}.lastSortedData`]
    }

    // Currently handles only single sorts
    const key = sortModel && Object.keys( sortModel ).length > 0 && Object.keys( sortModel )[0]

    if ( key ) {
      const model = sortModel[key]
      const direction = model.direction
      const accessor = Util.getAccess( this.lookup[key] )
      const sortFunction = SortController.SortFactory.create( model )

      const nData = [ ...data ].sort( ( a, b ) => {
        const valA = accessor( a )
        const valB = accessor( b )

        if ( direction === 'asc' ) {
          return sortFunction( valA, valB )
        }

        return sortFunction( valB, valA )
      } )

      SortController.cache[`${this.stateKey}.lastData`] = data
      SortController.cache[`${this.stateKey}.lastSortedData`] = nData
      SortController.cache[`${this.stateKey}.lastSortModel`] = sortModel

      return SortController.cache[`${this.stateKey}.lastSortedData`]
    }

    SortController.cache[`${this.stateKey}.lastData`] = data
    SortController.cache[`${this.stateKey}.lastSortedData`] = data
    SortController.cache[`${this.stateKey}.lastSortModel`] = sortModel

    return data 
  }

  getSortDirection( column, sorts ) {
    const key = this.key( column )

    if ( sorts[key] ) {
      return sorts[key].direction
    }

    return null
  }

  set( sorts ) {
    return Sort.set( {
      ...sorts
    }, this.stateKey )
  }

  toggle( key ) {
    return Sort.toggle( key, this.stateKey )
  }

  remove( keys ) {
    const keysToRemove = typeof keys === 'string' ? [ keys ] : keys

    return Sort.remove( keysToRemove, this.stateKey )
  }

  clear() {
    return Sort.clear( this.stateKey )
  }

  clearAll() {
    return Sort.clearAll()
  }

}
